"use client";

import { useState } from "react";

interface NoticeItem {
    key: string;
    kind: string;
    ref: string;
    author: string;
    policy: string;
    rationale?: string;
}

export function AdverseActionNotice({
    item, issuerName, onCancel, onConfirm,
}: { item: NoticeItem; issuerName: string; onCancel: () => void; onConfirm: (notice: string) => void }) {
    const reason = item.rationale || "Does not meet the governance standard at this time.";
    const date = new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
    const [confirming, setConfirming] = useState(false);

    const notice = [
        `Recipient: ${item.author}`,
        `Action: Submitted ${item.kind} on ${item.ref} was not accepted.`,
        `Policy basis: ${item.policy} — reviewed under the ratified governance model.`,
        `Reason: ${reason}`,
        `Review rights: You may request reconsideration in writing within 30 days; a neutral reviewer will respond. No final adverse decision is automated (Automation Authority, XC-1).`,
        `Contact: Governance Committee · policy-engine@cmtassociation.org`,
        `Issued: ${date} · by ${issuerName}`,
    ].join("\n");

    const confirm = async () => {
        setConfirming(true);
        try {
            await onConfirm(notice);
        } finally {
            setConfirming(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[110] bg-[rgba(12,34,38,0.45)] flex items-center justify-center p-6">
            <div className="bg-white rounded-2xl w-[560px] max-w-full shadow-2xl overflow-hidden" style={{ borderTop: "5px solid #ae232d" }}>
                <div className="px-6 py-5 border-b border-[#eef1ef]">
                    <div className="text-[11px] tracking-wide font-bold text-[#ae232d]">XC-3 · ADVERSE ACTION NOTICE (AUTO-GENERATED)</div>
                    <div className="font-bold text-lg mt-1 text-[#15302f]">Rejection requires a compliant notice</div>
                </div>
                <div className="px-6 py-5 text-[13px] leading-relaxed text-[#2c3e40]">
                    <div className="grid grid-cols-[140px_1fr] gap-x-3.5 gap-y-2">
                        <div className="text-[#88a39b] font-bold">Recipient</div><div>{item.author}</div>
                        <div className="text-[#88a39b] font-bold">Action</div><div>Submitted {item.kind} on {item.ref} was <strong>not accepted</strong>.</div>
                        <div className="text-[#88a39b] font-bold">Policy basis</div><div>{item.policy} — reviewed under the ratified governance model.</div>
                        <div className="text-[#88a39b] font-bold">Reason</div><div>{reason}</div>
                        <div className="text-[#88a39b] font-bold">Review rights</div><div>You may request reconsideration in writing within <strong>30 days</strong>; a neutral reviewer will respond. No final adverse decision is automated (Automation Authority, XC-1).</div>
                        <div className="text-[#88a39b] font-bold">Contact</div><div>Governance Committee · policy-engine@cmtassociation.org</div>
                        <div className="text-[#88a39b] font-bold">Issued</div><div>{date} · by {issuerName}</div>
                    </div>
                </div>
                <div className="px-6 py-4 bg-[#f4f6f5] border-t border-[#e6ebe9] flex justify-end gap-2.5">
                    <button onClick={onCancel} className="border border-[#d9dedb] bg-white text-[#5f7173] text-[13px] font-bold rounded-lg px-4 py-2">
                        Cancel rejection
                    </button>
                    <button onClick={confirm} disabled={confirming} className="border-none bg-[#ae232d] text-white text-[13px] font-bold rounded-lg px-4.5 py-2 disabled:opacity-50">
                        {confirming ? "Issuing…" : "Issue notice & reject"}
                    </button>
                </div>
            </div>
        </div>
    );
}
